import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel, ButtonSize, type SelectedFileStatus } from '@vizit/atoms';
import { FormDialog, type FormDialogProps, FormDropZone } from '@vizit/forms';
import { PlusIcon } from '@vizit/icons';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { UploadProductAssetDialogVariant } from './types';

type UploadProductAssetFormSchema = {
  files: File[];
};

interface UploadProductAssetDialogProps
  extends Omit<
    FormDialogProps<UploadProductAssetFormSchema>,
    'formName' | 'formMethods' | 'onSubmit' | 'onResetForm' | 'children'
  > {
  buttonClassName?: string;
  /**
   * Size of the trigger button.
   * @default ButtonSize.Sm
   */
  triggerSize?: ButtonSize;
  /**
   * Label rendered on the trigger button.
   * @default 'Upload assets'
   */
  triggerLabel?: React.ReactNode;
  /**
   * Default values for the form
   */
  defaultValues?: UploadProductAssetFormSchema;
  /**
   * Upload status from the form to display in the dialog
   */
  uploadStatus?: SelectedFileStatus;
  /**
   * Whether the dialog is open
   */
  isOpen?: boolean;
  /**
   * Variant of the dialog
   */
  variant: UploadProductAssetDialogVariant;
  /**
   * Callback function to handle the form submission
   */
  onSubmit: (data: UploadProductAssetFormSchema) => Promise<void>;
}

function UploadProductAssetDialog(props: UploadProductAssetDialogProps) {
  const {
    buttonClassName,
    triggerSize = ButtonSize.Sm,
    triggerLabel = 'Upload assets',
    defaultValues = {
      files: undefined,
    },
    uploadStatus,
    isOpen,
    variant = UploadProductAssetDialogVariant.Carousel,
    onSubmit,
    ...other
  } = props;

  // Form and validation schema
  const formSchema = yup.object().shape({
    files: yup.mixed<File[]>().required('Files are required'),
  });

  const methods = useForm<UploadProductAssetFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues,
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, setError, reset } = methods;

  const handleOnFileValidationError = (error?: string) => {
    if (error) {
      setError('files', { type: 'custom', message: error });
    }
  };

  const handleOnResetForm = () => {
    reset({
      files: undefined,
    });
  };

  return (
    <FormDialog
      formName={`upload-${variant}-asset`}
      formMethods={methods}
      isOpen={isOpen}
      onSubmit={handleSubmit(onSubmit)}
      onResetForm={handleOnResetForm}
      {...other}
    >
      <FormDialog.Trigger>
        <Button
          className={buttonClassName}
          aria-label={typeof triggerLabel === 'string' ? triggerLabel : 'Upload assets'}
          level={ButtonLevel.Secondary}
          size={triggerSize}
          iconStart={
            <span className="stroke-gray-utility-500">
              <PlusIcon color="stroke-inherit" size={18} />
            </span>
          }
        >
          {triggerLabel}
        </Button>
      </FormDialog.Trigger>
      <FormDialog.Title>Upload {variant} assets</FormDialog.Title>
      <FormDialog.Subtitle>Add one or more images to your product assets.</FormDialog.Subtitle>
      <FormDialog.FormFields>
        <FormDropZone
          allowsMultiple={true}
          control={control}
          name="files"
          uploadStatus={uploadStatus}
          onFileValidationError={handleOnFileValidationError}
        />
      </FormDialog.FormFields>
      <FormDialog.SubmitButton>Upload</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { UploadProductAssetDialog };
export type { UploadProductAssetFormSchema, UploadProductAssetDialogProps };
